import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAccountLinks } from '@atlassian/account';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { testIdGenerate } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import {
	getIdentityEnv,
	getContinueUrl,
} from '@atlassian/jira-atlassian-navigation-nav4-common/src/common/utils.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import {
	ANALYTICS_CONTEXT,
	AccountMenuIntlProvider,
	AccountMenuLoggedOutState,
} from '@atlassian/account-menu';
import type { Nav4Profile$key } from '@atlassian/jira-relay/src/__generated__/Nav4Profile.graphql';
import { LOGIN } from '@atlassian/jira-atlassian-navigation-nav4-common/src/common/constants.tsx';
import { Nav4AccountMenu } from './account-menu/Nav4AccountMenu.tsx';

type Nav4ProfileProps = {
	queryReference: Nav4Profile$key;
};

export function Nav4Profile({ queryReference }: Nav4ProfileProps): React.JSX.Element {
	const isAnonymous = useIsAnonymous();

	return (
		<NavigationAnalyticsContext data={ANALYTICS_CONTEXT}>
			<AccountMenuIntlProvider>
				{isAnonymous ? (
					<Nav4ProfileAnonymous />
				) : (
					<Nav4ProfileLoggedIn queryReference={queryReference} />
				)}
			</AccountMenuIntlProvider>
		</NavigationAnalyticsContext>
	);
}

function Nav4ProfileLoggedIn({ queryReference }: Nav4ProfileProps) {
	const data = useFragment<Nav4Profile$key>(
		graphql`
			fragment Nav4Profile on Query {
				user(accountId: $atlassianAccountId) {
					picture
					... on AtlassianAccountUser {
						email
					}
				}
			}
		`,
		queryReference,
	);

	return (
		<Nav4AccountMenu
			testId={testIdGenerate('secondary-actions', 'profile')}
			picture={data?.user?.picture}
			email={data?.user?.email ?? ''}
		/>
	);
}

function Nav4ProfileAnonymous() {
	const environment = useEnvironment();
	const identityEnv = getIdentityEnv(environment);

	const links = useAccountLinks({
		email: '',
		identityEnvironment: identityEnv,
		application: 'jira',
		continueUrl: getContinueUrl({ type: LOGIN }),
		isLoggedOut: true,
	});

	return (
		links?.logInLink && (
			<AccountMenuLoggedOutState
				testId={testIdGenerate('secondary-actions', 'profile')}
				logInLink={links.logInLink}
			/>
		)
	);
}
